import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

// Module-level cache shared across the app (separate slots for auth vs guest).
let _cachedPromise = null;
let _cachedGuestPromise = null;

function fetchCategoriesOnce(guest) {
  if (guest) {
    if (!_cachedGuestPromise) {
      _cachedGuestPromise = api.get("/public/categories").then((r) => r.data).catch((e) => {
        _cachedGuestPromise = null;
        throw e;
      });
    }
    return _cachedGuestPromise;
  }
  if (!_cachedPromise) {
    _cachedPromise = api.get("/categories").then((r) => r.data).catch((e) => {
      _cachedPromise = null;
      throw e;
    });
  }
  return _cachedPromise;
}

export function invalidateCategoriesCache() {
  _cachedPromise = null;
  _cachedGuestPromise = null;
}

/**
 * Fetches active categories (auth or public/guest).
 * Returns { categories, loading, refresh, labelFor, byId }.
 */
export function useCategories(guest = false) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    invalidateCategoriesCache();
    setLoading(true);
    try {
      const data = await fetchCategoriesOnce(guest);
      setCategories(data);
    } catch (e) {
      console.error("[useCategories] refresh failed:", e);
    }
    setLoading(false);
  }, [guest]);

  useEffect(() => {
    let cancelled = false;
    fetchCategoriesOnce(guest)
      .then((d) => {
        if (!cancelled) {
          setCategories(d);
          setLoading(false);
        }
      })
      .catch((e) => {
        console.error("[useCategories]", e);
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [guest]);

  const labelFor = useCallback(
    (id) => categories.find((x) => x.category_id === id)?.label || id,
    [categories]
  );

  const byId = useCallback(
    (id) => categories.find((c) => c.category_id === id) || null,
    [categories]
  );

  return { categories, loading, refresh, labelFor, byId };
}
